import React, { Component } from 'react';
import { toWire } from './registry';

// True-WYSIWYG web preview: embeds the real Next.js /preview/homepage route in an
// iframe and streams the in-progress layout to it via postMessage (Storyblok-bridge
// style). Falls back to the CMS-rendered preview when REACT_APP_WEB_PREVIEW_URL is unset.
// Env: REACT_APP_WEB_PREVIEW_URL = e.g. http://localhost:3100/preview/homepage
//
// The iframe renders at a REAL device viewport (desktop 1280, mobile 390) and CSS-scales
// to fit the pane. At the pane's literal ~760px it would fall under the 768px mobile
// breakpoint and wrongly show the mobile layout in "Desktop" mode.
const MESSAGE_TYPE = 'KA_HPB_PREVIEW';
const READY_TYPE = 'KA_HPB_PREVIEW_READY';
const DESKTOP_VIEWPORT = 1280;
const MOBILE_VIEWPORT = 390;

export default class WebPreviewFrame extends Component {
  constructor(props) {
    super(props);
    this.iframeRef = React.createRef();
    this.outerRef = React.createRef();
    this.state = { w: 0, h: 0 };
    // Fail closed: only a valid absolute http(s) URL yields a concrete target origin.
    // A malformed URL disables live preview rather than falling back to "*" (which
    // would broadcast the layout to any origin and accept READY from any origin).
    this.targetOrigin = (() => {
      try {
        const u = new URL(props.url);
        if (u.protocol === 'http:' || u.protocol === 'https:') return u.origin;
      } catch (e) { /* invalid URL */ }
      return null;
    })();
  }

  componentDidMount() {
    window.addEventListener('message', this.onMessage);
    this.measure();
    if (typeof ResizeObserver !== 'undefined' && this.outerRef.current) {
      this.ro = new ResizeObserver(this.measure);
      this.ro.observe(this.outerRef.current);
    } else {
      window.addEventListener('resize', this.measure);
    }
  }

  componentDidUpdate(prev) {
    // Re-send whenever the layout, device, or category changes.
    if (prev.sections !== this.props.sections || prev.device !== this.props.device || prev.category !== this.props.category) {
      this.post();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.onMessage);
    if (this.ro) this.ro.disconnect();
    else window.removeEventListener('resize', this.measure);
  }

  measure = () => {
    const el = this.outerRef.current;
    if (el) this.setState({ w: el.clientWidth, h: el.clientHeight });
  };

  onMessage = (event) => {
    if (!this.targetOrigin || event.origin !== this.targetOrigin) return;
    // READY = the iframe just (re)loaded and lost its state — always re-send (force).
    if (event.data && event.data.type === READY_TYPE) this.post(true);
  };

  // force=true bypasses the dedup (used on iframe load/READY, when the frame has no
  // state yet). Prop-change re-sends dedup on the serialized payload so an identical
  // layout isn't re-emitted on every keystroke (which would re-render the whole preview
  // and reset carousel/scroll state).
  post = (force) => {
    if (!this.targetOrigin) return;
    const frame = this.iframeRef.current;
    if (!frame || !frame.contentWindow) return;
    const layout = (this.props.sections || []).map((s) => toWire(s, 'web'));
    const isMobile = this.props.device === 'mobile';
    const category = this.props.category || 'allCategory';
    const serialized = JSON.stringify({ layout, isMobile, category });
    if (!force && serialized === this._lastSent) return;
    this._lastSent = serialized;
    frame.contentWindow.postMessage({ type: MESSAGE_TYPE, layout, isMobile, category }, this.targetOrigin);
  };

  render() {
    if (!this.targetOrigin) {
      return (
        <div className="d-flex align-items-center justify-content-center text-muted p-4" style={{ height: '100%', fontSize: '.85rem', textAlign: 'center' }}>
          Live preview is misconfigured: <code className="mx-1">REACT_APP_WEB_PREVIEW_URL</code> must be a valid absolute http(s) URL.
        </div>
      );
    }
    const mobile = this.props.device === 'mobile';
    const target = mobile ? MOBILE_VIEWPORT : DESKTOP_VIEWPORT;
    const { w, h } = this.state;
    // Scale the real-viewport iframe down to fit the pane (never upscale).
    const scale = w ? Math.min(1, w / target) : (mobile ? 1 : 0.6);
    const dispW = Math.round(target * scale);
    const iframeH = h ? Math.round(h / scale) : '100%';
    return (
      <div ref={this.outerRef} style={{ width: '100%', height: '100%', overflow: 'hidden', display: 'flex', justifyContent: 'center', background: '#e9ecef' }}>
        <div style={{ width: dispW || '100%', height: '100%', overflow: 'hidden', boxShadow: '0 0 0 1px rgba(0,0,0,.06)', background: '#fff' }}>
          <iframe
            ref={this.iframeRef}
            title="Web homepage preview"
            src={this.props.url}
            onLoad={() => this.post(true)}
            style={{ width: target, height: iframeH, border: 0, background: '#fff', display: 'block', transform: `scale(${scale})`, transformOrigin: '0 0' }}
          />
        </div>
      </div>
    );
  }
}
